import { useState, useEffect, useCallback, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { format, parse, subDays, startOfDay } from 'date-fns';
import ProductSelector from './ProductSelector';
import TimeRangeSelector from './TimeRangeSelector';
import Toggle from './Toggle';
import LLMPositionChart from './LLMPositionChart';
import LLMQueriesDisplay from './LLMQueriesDisplay';
import LLMPersonaSelector, { ALL_PERSONAS, apiPersona } from './LLMPersonaSelector';
import { apiPost } from '../services/apiClient';
import '../styles/NPSPage.css';
import '../styles/LLMRecommendationsPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MAIN_COLOR = { line: '#0057FF', fill: 'rgba(0, 87, 255, 0.1)' };
const COMPETITOR_COLORS = [
    { line: '#FF8A00', fill: 'rgba(255, 138, 0, 0.1)' },
    { line: '#00A86B', fill: 'rgba(0, 168, 107, 0.1)' },
    { line: '#7A1FFF', fill: 'rgba(122, 31, 255, 0.1)' },
    { line: '#E63946', fill: 'rgba(230, 57, 70, 0.1)' },
    { line: '#0891B2', fill: 'rgba(8, 145, 178, 0.1)' },
    { line: '#CA8A04', fill: 'rgba(202, 138, 4, 0.1)' },
];

const PERIODS = ['10d', '30d', '90d', '6mo', '1y', 'all'];
const PERIOD_LABELS = { '10d': '10D', '30d': '30D', '90d': '90D', '6mo': '6M', '1y': '1Y', 'all': 'All' };
const PERIOD_DAYS = { '10d': 10, '30d': 30, '90d': 90, '6mo': 180, '1y': 365, 'all': 730 };

const PROMPT_TYPES = ['both', 'static', 'dynamic'];
const PROMPT_TYPE_LABELS = { both: 'Static + Dynamic', static: 'Static', dynamic: 'Dynamic' };
const apiPromptType = (pt) => (pt === 'static' || pt === 'dynamic' ? pt : null);

function rangeDates(timeRange) {
    const days = PERIOD_DAYS[timeRange] || 90;
    return {
        startDate: format(subDays(new Date(), days), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    };
}

// Summary card: one brand's standing over the whole visible range.
const BrandCard = ({ name, color, position, appearRate, opportunities }) => (
    <div className="metric-card" style={{ borderTop: `3px solid ${color}` }}>
        <h3>{name}</h3>
        <div className="metric-row">
            <span className="metric-label">Avg Position</span>
            <span className="metric-value">{position != null ? position.toFixed(2) : '-'}</span>
        </div>
        <div className="metric-row">
            <span className="metric-label">Appearance Rate</span>
            <span className="metric-value">{appearRate != null ? `${Math.round(appearRate * 100)}%` : '-'}</span>
        </div>
        <div className="sample-size">Opportunities: {opportunities || 0}</div>
    </div>
);

// initialTimeRange lets the report generator's off-screen capture open the
// page on a specific window; defaults to the interactive page's 90d.
const LLMCompetitiveComparisonPage = ({ topicMap, initialTimeRange = '90d' }) => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProductName, setSelectedProductName] = useState('');
    const [timeRange, setTimeRange] = useState(initialTimeRange);
    const [promptType, setPromptType] = useState('both');
    const [persona, setPersona] = useState(ALL_PERSONAS);
    const [penalizeAbsence, setPenalizeAbsence] = useState(true);

    const [rows, setRows] = useState([]);   // flat [{ day, topic_id, ... }]
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [queries, setQueries] = useState([]);
    const [showQueries, setShowQueries] = useState(false);
    const [queriesLoading, setQueriesLoading] = useState(false);
    const [drilldownTitle, setDrilldownTitle] = useState('');

    const competitors = useMemo(
        () => (selectedProduct && topicMap?.[selectedProduct]?.competitors) || [],
        [selectedProduct, topicMap]
    );

    useEffect(() => {
        if (selectedProduct && topicMap?.[selectedProduct]) {
            setSelectedProductName(topicMap[selectedProduct].name || topicMap[selectedProduct].topic || '');
        }
    }, [selectedProduct, topicMap]);

    // Personas are topic-specific, so a selection can't carry across products.
    useEffect(() => {
        setPersona(ALL_PERSONAS);
    }, [selectedProduct]);

    // One request returns every brand's daily series — the peer group has to be
    // scored together, since a brand's penalty depends on the answers its rivals
    // were named in.
    useEffect(() => {
        if (!selectedProduct) return;
        const { startDate, endDate } = rangeDates(timeRange);
        const competitorIds = competitors.map(c => c.id).filter(Boolean);

        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        setShowQueries(false);
        setQueries([]);

        apiPost('topic/llm-recommendations/positions', {
            topic_id: selectedProduct,
            competitor_ids: competitorIds,
            start_date: startDate,
            end_date: endDate,
            prompt_type: apiPromptType(promptType),
            persona: apiPersona(persona),
        })
            .then(async r => {
                // A failed request must not read as "no data" — an empty chart
                // and a broken endpoint look identical otherwise.
                if (!r.ok) {
                    throw new Error(r.status === 404
                        ? 'Endpoint not found (is the backend running the current build?)'
                        : `Request failed (${r.status})`);
                }
                return r.json();
            })
            .then(result => { if (!cancelled) setRows(Array.isArray(result?.data) ? result.data : []); })
            .catch(err => {
                console.error('[LLMCompetitiveComparisonPage] positions error:', err);
                if (!cancelled) { setRows([]); setLoadError(err.message || 'Could not load position data.'); }
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });

        return () => { cancelled = true; };
    }, [selectedProduct, timeRange, competitors, promptType, persona]);

    // Brand id -> display name, for every topic we might plot.
    const nameFor = useCallback((topicId) => {
        if (topicId === selectedProduct) return selectedProductName || 'Selected Product';
        const c = competitors.find(x => x.id === topicId);
        return c?.topic || c?.alt_name || topicMap?.[topicId]?.name || topicId;
    }, [competitors, selectedProduct, selectedProductName, topicMap]);

    // Group the flat rows by brand, main product first.
    const byTopic = useMemo(() => {
        const groups = new Map();
        rows.forEach(r => {
            if (!groups.has(r.topic_id)) groups.set(r.topic_id, []);
            groups.get(r.topic_id).push(r);
        });
        const ordered = [...groups.entries()].sort(([a], [b]) => {
            if (a === selectedProduct) return -1;
            if (b === selectedProduct) return 1;
            return 0;
        });
        // Count competitors independently of position in `ordered` — the main
        // product is not guaranteed to be present, and a positional index would
        // then run off the front of COMPETITOR_COLORS.
        let competitorIndex = 0;
        return ordered.map(([topicId, series]) => {
            const isMain = topicId === selectedProduct;
            const colorSet = isMain
                ? MAIN_COLOR
                : COMPETITOR_COLORS[competitorIndex++ % COMPETITOR_COLORS.length];
            return { topicId, name: nameFor(topicId), rows: series, colorSet, isMain };
        });
    }, [rows, selectedProduct, nameFor]);

    // A brand with no opportunities anywhere in range has nothing to say.
    const plotted = useMemo(
        () => byTopic.filter(s => s.rows.some(r => (r.window_opportunities || 0) > 0)),
        [byTopic]
    );

    const hasData = plotted.length > 0;

    const chartData = useMemo(() => {
        if (!hasData) return null;

        const daySet = new Set();
        plotted.forEach(s => s.rows.forEach(r => daySet.add(r.day)));
        const labels = [...daySet].sort();

        const datasets = plotted.map(s => {
            const byDay = {};
            s.rows.forEach(r => { byDay[r.day] = r; });
            return {
                label: s.name,
                data: labels.map(d => {
                    const r = byDay[d];
                    const value = r
                        ? (penalizeAbsence ? r.avg_position : r.avg_position_present)
                        : null;
                    return {
                        x: d,
                        y: value != null ? Number(value) : null,
                        positionPresent: r?.avg_position_present,
                        windowAppearances: r?.window_appearances,
                        windowOpportunities: r?.window_opportunities,
                    };
                }),
                borderColor: s.colorSet.line,
                backgroundColor: s.colorSet.fill,
                borderWidth: s.isMain ? 3 : 2,
                tension: 0.1,
                pointRadius: 2,
                pointHoverRadius: 5,
                spanGaps: true,
                topicId: s.topicId,
            };
        });

        return { labels, datasets };
    }, [plotted, penalizeAbsence, hasData]);

    // Range-wide summary per brand, pooled over the raw daily counts (not an
    // average of the smoothed points).
    const summaries = useMemo(() => plotted.map(s => {
        let opportunities = 0, appearances = 0;
        s.rows.forEach(r => {
            opportunities += r.opportunities || 0;
            appearances += r.appearances || 0;
        });

        // Weight each day's smoothed value by that day's opportunities so quiet
        // days don't count as much as busy ones.
        let weighted = 0, weight = 0;
        s.rows.forEach(r => {
            const v = penalizeAbsence ? r.avg_position : r.avg_position_present;
            const w = penalizeAbsence ? (r.opportunities || 0) : (r.appearances || 0);
            if (v != null && w > 0) { weighted += Number(v) * w; weight += w; }
        });

        return {
            topicId: s.topicId,
            name: s.name,
            color: s.colorSet.line,
            position: weight > 0 ? weighted / weight : null,
            appearRate: opportunities > 0 ? appearances / opportunities : null,
            opportunities,
        };
    }).sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity)), [plotted, penalizeAbsence]);

    // Drill-down scoped to the same universe the chart counts: open prompts, no
    // confidence gate (presence is never confidence-gated), and answers that
    // never named the brand included — on this tab that absence IS the finding.
    const fetchQueries = useCallback(async (topicId, startDate, endDate, title) => {
        if (!topicId) return;
        setQueriesLoading(true);
        setDrilldownTitle(title);
        try {
            const res = await apiPost('topic/llm-recommendations/queries', {
                topic_id: topicId,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                limit: 100,
                prompt_type: apiPromptType(promptType),
                question_type: 'open',
                min_confidence: null,
                include_absent: true,
                persona: apiPersona(persona),
            });
            const result = await res.json();
            setQueries(result?.data || []);
            setShowQueries(true);
        } catch (err) {
            console.error('[LLMCompetitiveComparisonPage] queries error:', err);
            setQueries([]);
            setShowQueries(true);
        } finally {
            setQueriesLoading(false);
        }
    }, [promptType, persona]);

    const handleChartPointClick = useCallback(({ label, datasetIndex }) => {
        if (!label) return;
        const ds = chartData?.datasets?.[datasetIndex];
        const topicId = ds?.topicId;
        if (!topicId) return;
        const dayStart = startOfDay(parse(label, 'yyyy-MM-dd', new Date()));
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
        fetchQueries(topicId, dayStart, dayEnd, `${ds.label} — ${format(dayStart, 'MMMM d, yyyy')}`);
    }, [chartData, fetchQueries]);

    return (
        <div className="nps-page-container">
            <div className="controls-section">
                <div className="product-control">
                    <ProductSelector
                        topicMap={topicMap}
                        selectedId={selectedProduct || ''}
                        onSelect={setSelectedProduct}
                    />
                    <LLMPersonaSelector
                        topicId={selectedProduct}
                        value={persona}
                        onChange={setPersona}
                    />
                    <Toggle
                        label="Penalize Non-Appearance"
                        checked={penalizeAbsence}
                        onChange={setPenalizeAbsence}
                    />
                </div>
                <div className="view-controls">
                    <TimeRangeSelector
                        timeRange={promptType}
                        onTimeRangeChange={setPromptType}
                        periods={PROMPT_TYPES}
                        periodLabels={PROMPT_TYPE_LABELS}
                        defaultPeriod="both"
                    />
                    <TimeRangeSelector
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        periods={PERIODS}
                        periodLabels={PERIOD_LABELS}
                        defaultPeriod="90d"
                    />
                </div>
            </div>

            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress size={40} />
                </div>
            ) : (
                <>
                    <div className="chart-section">
                        {loadError ? (
                            <div style={{ textAlign: 'center', padding: '48px 16px', color: '#b91c1c' }}>
                                Could not load position data: {loadError}
                            </div>
                        ) : hasData ? (
                            <>
                                <LLMPositionChart
                                    chartData={chartData}
                                    onPointClick={handleChartPointClick}
                                    penalized={penalizeAbsence}
                                    yAxisTitle={penalizeAbsence
                                        ? 'Average Position (absences penalized)'
                                        : 'Average Position When Named'}
                                />
                                <p className="llm-metric-note">
                                    {penalizeAbsence
                                        ? 'Measured on open-ended category prompts that name no brand, so every rival is genuinely in contention. A brand missing from an answer is scored one place behind the last brand named — but only when the answer ranked a real list (three or more brands); missing from a one- or two-brand answer costs nothing. Positions are averaged as reciprocal ranks (the search-ranking standard), so one deep placement in a long list barely moves a brand that usually sits near the top. Toggle off to average only the answers a brand actually appeared in.'
                                        : 'Averages only the answers where the brand was actually named, ignoring how often it was left out entirely. Positions are averaged as reciprocal ranks, so a single deep placement cannot swamp consistent top spots. A brand can look strong here yet rarely appear at all — check the appearance rate.'}
                                </p>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
                                {!selectedProduct
                                    ? 'Select a product to compare positions.'
                                    : persona !== ALL_PERSONAS
                                        ? `No open-ended category prompts asked as "${persona}" in the selected period. Persona prompts are mostly brand-specific or head-to-head, which this position metric does not count — switch to All Personas to see the full picture.`
                                        : 'No open-ended category prompts with position data for this product in the selected period.'}
                            </div>
                        )}
                    </div>

                    {hasData && (
                        <div className="metrics-section">
                            <div className="metric-cards">
                                {summaries.map(s => (
                                    <BrandCard
                                        key={s.topicId}
                                        name={s.name}
                                        color={s.color}
                                        position={s.position}
                                        appearRate={s.appearRate}
                                        opportunities={s.opportunities}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {queriesLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <CircularProgress size={28} />
                </div>
            )}

            {showQueries && !queriesLoading && (
                queries.length > 0 ? (
                    <LLMQueriesDisplay
                        queries={queries}
                        onClose={() => { setShowQueries(false); setQueries([]); }}
                        id="llm-position-queries"
                        title={drilldownTitle || 'Prompts & ChatGPT Responses'}
                        autoScroll
                        statName="LLM Rec"
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
                        This brand was not named in any open-ended prompt on this day.
                    </div>
                )
            )}
        </div>
    );
};

export default LLMCompetitiveComparisonPage;
